import { useState, type ReactNode } from 'react';

// Pasos a realizar en el sitio de ARCA durante el onboarding. Los nombres de servicios, opciones y
// botones salen de la documentación oficial de ARCA: "¿Cómo obtener el Certificado Digital para
// entorno de producción?", "Delegación de Webservices con el Administrador de Relaciones" y
// "Puntos de Venta - Para qué se utiliza". Si ARCA cambia sus pantallas, actualizar estos textos.

export const ARCA_URL = 'https://www.arca.gob.ar';

export function GuideStep(props: {
  number: number;
  title: string;
  where?: string;
  done?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="card step">
      <div className="step-header">
        <span className={`step-number${props.done ? ' done' : ''}`} aria-hidden="true">
          {props.done ? '✓' : props.number}
        </span>
        <div>
          <h3>
            Paso {props.number} · {props.title}
            {props.done && <span className="badge approved step-badge">Listo</span>}
          </h3>
          {props.where && <div className="step-where">{props.where}</div>}
        </div>
      </div>
      <div className="step-body">{props.children}</div>
    </section>
  );
}

function Ui({ children }: { children: ReactNode }) {
  return <strong className="ui-label">{children}</strong>;
}

function CopyAlias({ alias }: { alias: string }) {
  const [copied, setCopied] = useState<'idle' | 'ok' | 'fail'>('idle');

  async function copy() {
    try {
      await navigator.clipboard.writeText(alias);
      setCopied('ok');
    } catch {
      // El iframe del host puede no permitir el portapapeles.
      setCopied('fail');
    }
  }

  return (
    <span className="alias-box">
      <code>{alias}</code>
      <button type="button" className="btn secondary small" onClick={copy}>
        Copiar
      </button>
      {copied === 'ok' && <span className="muted">Copiado</span>}
      {copied === 'fail' && <span className="muted">Seleccioná el texto y copialo</span>}
    </span>
  );
}

export function BeforeYouStart({ taxId }: { taxId: string }) {
  return (
    <section className="card">
      <h3>Antes de empezar</h3>
      <ul className="guide-list">
        <li>
          Necesitás la <strong>clave fiscal nivel 3</strong> del titular de la CUIT {taxId}, o de quien administra sus
          relaciones en ARCA.
        </li>
        <li>
          Vas a alternar entre este sistema y el sitio de ARCA. Los pasos marcados <em>En ARCA</em> se hacen en{' '}
          <a href={ARCA_URL} target="_blank" rel="noopener noreferrer">
            arca.gob.ar
          </a>{' '}
          ingresando con <Ui>Iniciar sesión</Ui>.
        </li>
        <li>
          El proceso se hace una vez por CUIT. El certificado de ARCA tiene fecha de vencimiento, que vas a ver arriba
          una vez cargado.
        </li>
      </ul>
    </section>
  );
}

export function EnableCertificatesService() {
  return (
    <>
      <ol className="guide-list">
        <li>
          Ingresá a ARCA y buscá el servicio <Ui>Administración de Certificados Digitales</Ui>. Si ya aparece, seguí con
          el paso 3.
        </li>
        <li>
          Si no aparece, entrá a <Ui>Administrador de Relaciones de Clave Fiscal</Ui>.
        </li>
        <li>
          Hacé clic en <Ui>Nueva Relación</Ui> y después en <Ui>Buscar</Ui> para elegir el servicio.
        </li>
        <li>
          Elegí <Ui>Administración de Certificados Digitales</Ui>.
        </li>
        <li>
          Para el representante, hacé clic en <Ui>Buscar</Ui>, ingresá tu CUIT/CUIL y confirmá con <Ui>Confirmar</Ui>.
        </li>
        <li>
          Cerrá la sesión de ARCA y volvé a ingresar para que aparezca el servicio. Si sigue sin aparecer, aceptá la
          relación desde el servicio <Ui>Aceptación de Designación</Ui>.
        </li>
      </ol>
    </>
  );
}

export function CreateCertificate({ taxId, alias }: { taxId: string; alias?: string }) {
  return (
    <ol className="guide-list">
      <li>
        Entrá a <Ui>Administración de Certificados Digitales</Ui>. Si representás a más de un contribuyente, elegí el de
        la CUIT {taxId}.
      </li>
      <li>
        Hacé clic en <Ui>Agregar alias</Ui>.
      </li>
      <li>
        Escribí un alias para identificar el certificado.{' '}
        {alias ? (
          <>
            Te sugerimos usar el mismo nombre del archivo descargado: <CopyAlias alias={alias} />
          </>
        ) : (
          'Te sugerimos usar el nombre del archivo descargado en el paso 1, sin la extensión .csr.'
        )}
      </li>
      <li>
        Subí el archivo <strong>.csr</strong> del paso 1 y hacé clic en <Ui>Agregar alias</Ui>.
      </li>
      <li>
        En la lista, hacé clic en <Ui>Ver</Ui> al lado del alias y descargá el certificado (archivo <strong>.crt</strong>).
      </li>
    </ol>
  );
}

export function AuthorizeInvoicing({ taxId }: { taxId: string }) {
  return (
    <>
      <ol className="guide-list">
        <li>
          Entrá a <Ui>Administrador de Relaciones de Clave Fiscal</Ui> y elegí en el desplegable el contribuyente de la
          CUIT {taxId}.
        </li>
        <li>
          Hacé clic en <Ui>Nueva Relación</Ui> y después en <Ui>Buscar</Ui> para elegir el servicio.
        </li>
        <li>
          Dentro de la agrupación <Ui>Webservices</Ui>, elegí <Ui>Facturación Electrónica</Ui>.
        </li>
        <li>
          Para el representante, elegí en el desplegable de computadores fiscales el <strong>alias que creaste en el
          paso 3</strong>.
        </li>
        <li>
          Hacé clic en <Ui>Confirmar</Ui> y, en la pantalla de revisión, otra vez en <Ui>Confirmar</Ui>.
        </li>
      </ol>
      <p className="note">
        Sin este paso el certificado existe, pero ARCA no lo autoriza a emitir comprobantes.
      </p>
    </>
  );
}

type IvaKind = 'ri' | 'monotributo' | 'exento' | 'unknown';

// La condición de la empresa puede venir normalizada o como texto libre en empresas históricas.
export function ivaKind(ivaCondition: string | undefined): IvaKind {
  const key = String(ivaCondition ?? '')
    .toLowerCase()
    .replace(/[\s_.-]/g, '');
  if (['responsableinscripto', 'ivaresponsableinscripto', 'ri'].includes(key)) return 'ri';
  if (['monotributo', 'monotributista', 'responsablemonotributo'].includes(key)) return 'monotributo';
  if (['exento', 'ivaexento', 'sujetoexento', 'ivasujetoexento'].includes(key)) return 'exento';
  return 'unknown';
}

const IVA_LABEL: Record<Exclude<IvaKind, 'unknown'>, string> = {
  ri: 'Responsable Inscripto',
  monotributo: 'Responsable Monotributo',
  exento: 'IVA Sujeto Exento',
};

// Mismo texto que el PDF; un valor histórico no reconocido se muestra tal cual.
export function ivaLabel(ivaCondition: string | undefined): string {
  const kind = ivaKind(ivaCondition);
  return kind === 'unknown' ? ivaCondition || '-' : IVA_LABEL[kind];
}

const SALE_POINT_SYSTEM = {
  ri: 'Factura Electrónica - RI IVA - Aplicativo y Webservices',
  monotributo: 'Factura Electrónica - Monotributo - Webservices',
};

export function CreateSalePoint({ taxId, ivaCondition }: { taxId: string; ivaCondition?: string }) {
  const kind = ivaKind(ivaCondition);

  return (
    <>
      <ol className="guide-list">
        <li>
          Entrá a <Ui>Administración de puntos de venta y domicilios</Ui>. Si no lo ves, sumalo desde{' '}
          <Ui>Administrador de Relaciones de Clave Fiscal</Ui> con <Ui>Adherir Servicio</Ui>.
        </li>
        <li>Elegí el contribuyente de la CUIT {taxId}.</li>
        <li>
          Entrá a <Ui>A/B/M de Puntos de venta</Ui> y creá un punto de venta nuevo.
        </li>
        <li>Completá el número (cinco dígitos, por ejemplo 00002) y el nombre de fantasía.</li>
        <li>
          En el sistema de facturación elegí la opción de <strong>Webservices</strong>:
          {kind === 'ri' && (
            <>
              {' '}
              <Ui>{SALE_POINT_SYSTEM.ri}</Ui>.
            </>
          )}
          {kind === 'monotributo' && (
            <>
              {' '}
              <Ui>{SALE_POINT_SYSTEM.monotributo}</Ui>.
            </>
          )}
          {kind === 'exento' && (
            <> para Exentos en IVA, consultá con tu contador cuál corresponde a tu actividad.</>
          )}
          {kind === 'unknown' && (
            <ul className="guide-list nested">
              <li>
                Responsable Inscripto: <Ui>{SALE_POINT_SYSTEM.ri}</Ui>.
              </li>
              <li>
                Monotributo: <Ui>{SALE_POINT_SYSTEM.monotributo}</Ui>.
              </li>
            </ul>
          )}
        </li>
        <li>Elegí el domicilio y confirmá el alta.</li>
      </ol>
      <p className="note">
        Los puntos de venta de <strong>Comprobantes en Línea</strong> o <strong>Factuweb</strong> no sirven para facturar
        desde este sistema: necesitás uno de <strong>Webservices</strong>.
      </p>
    </>
  );
}

export function GuideFootnote() {
  return (
    <p className="muted guide-footnote">
      Los nombres de servicios y botones corresponden a la documentación oficial de ARCA y pueden variar si ARCA actualiza
      sus pantallas.
    </p>
  );
}
